// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { DateInfoRow } from '@/components/date-info-row';
import { LabeledTruncatedText } from '@/components/labeled-truncated-text';
import { LocationLabel } from '@/components/location-label';
import {
  Inventory2Outlined,
  SettingsOutlined,
} from '@/constants/hardfin-standard-icons';
import { ExchangeOrder } from '@/exchange-order/api/types';
import { ExchangeOrderStatusChip } from '@/exchange-order/components/exchange-order-status-chip';
import {
  RETURN_ORDER_REASON_LABELS,
  reasonFromAPI,
} from '@/return-order/api/types';
import { formatNumber } from '@/utilities/number';
import { Stack, TableCell, TableRow, Tooltip, Typography } from '@mui/material';

/**
 * Props for the ExchangeOrderTableRow component.
 */
interface Props {
  exchangeOrder: ExchangeOrder;
}

/**
 * Creates tooltip content showing all exchange order dates.
 * Promise dates are derived from the linked RO/FO, not the EO itself.
 */
function getAllExchangeOrderDatesContent(exchangeOrder: ExchangeOrder) {
  // Get promise dates from linked return order (if any)
  const linkedRO = exchangeOrder.returnOrders[0];
  const roPromiseShipDate = linkedRO?.promiseShipDate;
  const roPromiseDeliveryDate = linkedRO?.promiseDeliveryDate;

  const dates = [
    {
      label: 'Created:',
      date:
        exchangeOrder.createdAt?.toLocaleString({ dateStyle: 'medium' }) ||
        null,
    },
    {
      label: 'RO promise ship:',
      date:
        roPromiseShipDate?.toLocaleString({
          dateStyle: 'medium',
        }) || null,
    },
    {
      label: 'RO promise delivery:',
      date:
        roPromiseDeliveryDate?.toLocaleString({
          dateStyle: 'medium',
        }) || null,
    },
    {
      label: 'Last updated:',
      date:
        exchangeOrder.lastUpdatedAt?.toLocaleString({ dateStyle: 'medium' }) ||
        null,
    },
  ];

  return (
    <Stack spacing={0.25}>
      {dates.map(({ label, date }) => (
        <DateInfoRow key={label} label={label} date={date} />
      ))}
    </Stack>
  );
}

/**
 * Gets the most relevant date for an exchange order based on its status.
 * Promise dates are derived from the linked RO, not the EO itself.
 */
function getRelevantDate(exchangeOrder: ExchangeOrder) {
  // Get promise dates from linked return order (if any)
  const linkedRO = exchangeOrder.returnOrders[0];
  const promiseShipDate = linkedRO?.promiseShipDate;
  const promiseDeliveryDate = linkedRO?.promiseDeliveryDate;

  if (
    exchangeOrder.status === 'complete' ||
    exchangeOrder.status === 'cancelled'
  ) {
    return { date: null, label: null };
  }

  if (exchangeOrder.status === 'draft') {
    if (promiseShipDate) {
      return {
        date: promiseShipDate.toLocaleString({
          dateStyle: 'medium',
        }),
        label: 'Promise ship',
      };
    }

    if (promiseDeliveryDate) {
      return {
        date: promiseDeliveryDate.toLocaleString({
          dateStyle: 'medium',
        }),
        label: 'Promise delivery',
      };
    }

    return {
      date: exchangeOrder.createdAt.toLocaleString({ dateStyle: 'medium' }),
      label: 'Created',
    };
  }

  if (exchangeOrder.status === 'in_progress') {
    if (promiseShipDate) {
      return {
        date: promiseShipDate.toLocaleString({
          dateStyle: 'medium',
        }),
        label: 'Promise ship',
      };
    }

    if (promiseDeliveryDate) {
      return {
        date: promiseDeliveryDate.toLocaleString({
          dateStyle: 'medium',
        }),
        label: 'Promise delivery',
      };
    }

    return {
      date: exchangeOrder.lastUpdatedAt.toLocaleString({ dateStyle: 'medium' }),
      label: 'Last updated',
    };
  }

  return { date: null, label: null };
}

/**
 * A table row component for displaying exchange order information.
 */
export function ExchangeOrderTableRow(props: Props) {
  const { exchangeOrder } = props;

  // Get type and reason from linked return order
  const linkedRO = exchangeOrder.returnOrders[0];
  const exchangeType = linkedRO ? reasonFromAPI(linkedRO.returnType) : null;
  const exchangeReason = linkedRO?.returnReason;

  return (
    <TableRow>
      {/* References Column */}
      <TableCell sx={{ verticalAlign: 'middle' }}>
        <Typography variant="subtitle2">
          {exchangeOrder.exchangeNumber}
        </Typography>
        <Stack
          direction="row"
          alignItems="flex-start"
          flexWrap="wrap"
          gap={0.3}
        >
          <Typography variant="subtitle2">Type:</Typography>
          {exchangeType && RETURN_ORDER_REASON_LABELS[exchangeType] ? (
            <Typography variant="body2">
              {RETURN_ORDER_REASON_LABELS[exchangeType]}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Not set
            </Typography>
          )}
        </Stack>
        {exchangeReason ? (
          <LabeledTruncatedText label="Reason:" text={exchangeReason} />
        ) : (
          <Stack
            direction="row"
            alignItems="flex-start"
            flexWrap="wrap"
            gap={0.3}
          >
            <Typography variant="subtitle2">Reason:</Typography>
            <Typography variant="body2" color="text.secondary">
              Not set
            </Typography>
          </Stack>
        )}
        {exchangeOrder.orderNotes ? (
          <LabeledTruncatedText label="Note:" text={exchangeOrder.orderNotes} />
        ) : (
          <Stack
            direction="row"
            alignItems="flex-start"
            flexWrap="wrap"
            gap={0.3}
          >
            <Typography variant="subtitle2">Note:</Typography>
            <Typography variant="body2" color="text.secondary">
              Not set
            </Typography>
          </Stack>
        )}
      </TableCell>

      {/* From/To Location Column */}
      <TableCell sx={{ verticalAlign: 'middle' }}>
        <Stack spacing={0.5} sx={{ width: '100%' }}>
          {exchangeOrder.locationPairs.map((pair, index) => (
            <Stack key={index} spacing={0.25}>
              <LocationLabel label="From:" location={pair.fromLocation} />
              <LocationLabel label="To:" location={pair.toLocation} />
            </Stack>
          ))}
          {exchangeOrder.locationPairs.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No locations
            </Typography>
          )}
        </Stack>
      </TableCell>

      {/* Items Column */}
      <TableCell sx={{ verticalAlign: 'middle' }}>
        <Stack spacing={0.3}>
          {exchangeOrder.items
            .slice(0, exchangeOrder.items.length > 4 ? 3 : 4)
            .map((lineItem, index) => {
              const ItemIcon = lineItem.item.hasAssets
                ? Inventory2Outlined
                : SettingsOutlined;
              return (
                <Stack
                  key={lineItem.item.id ?? index}
                  direction="row"
                  alignItems="flex-start"
                  flexWrap="wrap"
                  gap={0.3}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    flexWrap="wrap"
                    gap={0.5}
                  >
                    <ItemIcon sx={{ fontSize: '1rem' }} />
                    <>
                      <Typography variant="body2">
                        {lineItem.item.sku}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        ({lineItem.item.name})
                      </Typography>
                    </>
                    <Typography
                      variant="body2"
                      sx={{ color: 'text.secondary' }}
                    >
                      &times;{' '}
                      {formatNumber(lineItem.returnQuantity.requestedQuantity)}
                    </Typography>
                  </Stack>
                </Stack>
              );
            })}
          {exchangeOrder.items.length > 4 && (
            <Tooltip
              arrow
              title={
                <Stack spacing={0.5}>
                  {exchangeOrder.items.slice(3).map((lineItem, index) => {
                    const itemLabel = `${lineItem.item.sku} (${lineItem.item.name})`;
                    return (
                      <Typography
                        key={lineItem.item.id ?? `overflow-${index}`}
                        variant="caption"
                      >
                        {itemLabel} &times;{' '}
                        {formatNumber(
                          lineItem.returnQuantity.requestedQuantity
                        )}
                      </Typography>
                    );
                  })}
                </Stack>
              }
            >
              <Typography variant="body2" color="text.secondary">
                +{exchangeOrder.items.length - 3} more
              </Typography>
            </Tooltip>
          )}
        </Stack>
      </TableCell>

      {/* Status Column */}
      <TableCell sx={{ verticalAlign: 'middle' }}>
        <Stack
          spacing={1}
          alignItems="center"
          justifyContent="center"
          sx={{ height: '100%' }}
        >
          <Tooltip
            arrow
            placement="left"
            title={getAllExchangeOrderDatesContent(exchangeOrder)}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              gap={1}
              flexWrap="wrap"
              width="100%"
            >
              <ExchangeOrderStatusChip status={exchangeOrder.status} />
              {(() => {
                const relevantDate = getRelevantDate(exchangeOrder);
                if (exchangeOrder.status === 'complete') {
                  return null;
                }
                if (relevantDate.date) {
                  return (
                    <Stack alignItems="flex-end">
                      <Typography variant="body2" color="text.secondary">
                        {relevantDate.label && `${relevantDate.label}`}
                      </Typography>
                      <Typography variant="body2">
                        {relevantDate.date}
                      </Typography>
                    </Stack>
                  );
                }
                return (
                  <Typography variant="body2" color="text.secondary">
                    No date set
                  </Typography>
                );
              })()}
            </Stack>
          </Tooltip>
        </Stack>
      </TableCell>
    </TableRow>
  );
}
